import { useState, useEffect } from "react";
import { UserPlus, Shield, Loader2, Plus } from "lucide-react";

// TypeScript Interface for Role
interface Role {
  id: number | string;
  name: string;
  level?: number;
}

const AddUser = () => {
  // useState me <Role[]> lagaya hai
  const [roles, setRoles] = useState<Role[]>([]);
  const [formData, setFormData] = useState({ name: "", email: "", password: "", role_id: "" });
  
  // Custom Role State
  const [showNewRoleInput, setShowNewRoleInput] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const token = localStorage.getItem("sevaa_admin_token");

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      // const response = await fetch("http://localhost:1415/api/auth/roles", {
      // const response = await fetch("http://localhost/seva-backend/api/auth/roles.php", {
      const response = await fetch("https://www.sevaafoundation.com/seva-backend/api/auth/roles.php", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setRoles(data.roles);
        if (data.roles.length > 0) {
          setFormData(prev => ({ ...prev, role_id: data.roles[0].id.toString() }));
        }
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const handleAddRole = async () => {
    if (!newRoleName.trim()) return;
    try {
      // const response = await fetch("http://localhost:1415/api/auth/roles", {
      // const response = await fetch("http://localhost/seva-backend/api/auth/roles.php", {
      const response = await fetch("https://www.sevaafoundation.com/seva-backend/api/auth/roles.php", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newRoleName }),
      });
      const data = await response.json();
      if (data.success) {
        setRoles([...roles, data.role]);
        setFormData({ ...formData, role_id: data.role.id.toString() }); 
        setShowNewRoleInput(false);
        setNewRoleName("");
      }
    } catch (error) {
      console.error("Error creating role:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ text: "", type: "" });

    try {
      // const response = await fetch("http://localhost:1415/api/auth/users", {
      // const response = await fetch("http://localhost/seva-backend/api/auth/users.php", {
      const response = await fetch("https://www.sevaafoundation.com/seva-backend/api/auth/users.php", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ text: "User created successfully!", type: "success" });
        setFormData({ name: "", email: "", password: "", role_id: roles.length > 0 ? roles[0].id.toString() : "" });
      } else {
        setMessage({ text: data.error || "Failed to create user", type: "error" });
      }
    } catch (error) {
      setMessage({ text: "Server error occurred", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      <div className="max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-sm border border-slate-200">
        <div className="flex items-center gap-3 mb-8 pb-6 border-b border-slate-100">
          <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
            <UserPlus size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Add New User</h2>
            <p className="text-slate-500 text-sm">Create credentials and assign access levels.</p>
          </div>
        </div>

        {message.text && (
          <div className={`mb-6 p-4 rounded-lg text-sm font-medium ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name</label>
              <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500" placeholder="John Doe" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address</label>
              <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500" placeholder="user@sevaa.org" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Password</label>
            <input type="password" required value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500" placeholder="••••••••" />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-semibold text-slate-700">Assign Role</label>
              <button type="button" onClick={() => setShowNewRoleInput(!showNewRoleInput)} className="text-sm text-orange-600 font-medium hover:underline flex items-center gap-1">
                <Plus size={16} /> Create New Role
              </button>
            </div>

            {showNewRoleInput ? (
              <div className="flex gap-3 mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <input type="text" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} placeholder="e.g. Content Manager" className="flex-1 px-4 py-2 border border-slate-200 rounded-lg outline-none" />
                <button type="button" onClick={handleAddRole} className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 text-sm font-medium">Add</button>
              </div>
            ) : (
              <div className="relative">
                <Shield size={18} className="absolute left-4 top-3.5 text-slate-400" />
                <select value={formData.role_id} onChange={(e) => setFormData({...formData, role_id: e.target.value})} className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500 appearance-none text-slate-700">
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <button type="submit" disabled={isLoading} className="w-full mt-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3.5 rounded-xl shadow-lg transition-all flex justify-center items-center gap-2">
            {isLoading ? <><Loader2 size={20} className="animate-spin" /> Processing...</> : "Create User Profile"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddUser;